import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Field,
  FieldTypes,
  Inscription,
  useResponsive,
  validatePhoneNumber,
} from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { AccountAccessPayload } from '@/modules/user/components/SignUp/AccountAccess/useAccountAccess.store';
import { centeredFieldStyles } from '@/modules/user/components/SignUp/signUp.constants';
import { useAuthStore } from '@/modules/user/stores';

interface Props {
  errors?: Partial<AccountAccessPayload>;
  isOrphanMode?: boolean;
}

export default ({ isOrphanMode }: Props) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const email = useMemo(() => user?.preferredEmail, [user]);

  const fields = useMemo(
    () => [
      ...(!isOrphanMode
        ? [
            {
              name: 'email',
              type: FieldTypes.Email,
              label: (
                <Box flex={{ grow: 1 }}>
                  <Inscription margin={{ bottom: '4px' }} size="large" weight="bold" color="textBody">
                    {t('Personal email')}
                  </Inscription>
                  <Box>
                    <Inscription weight={500} color="textSecondary" size="12px" lineHeight="18px">
                      {t('It will be used for account verification.')}
                    </Inscription>
                  </Box>
                </Box>
              ),
              isLarge: true,
              placeholder: 'Email',
              enterKeyHint: 'next',
              showRequireIcon: true,
              defaultValue: email,
              validator: yup.string().email('Email must be valid').required(t('Email is required')),
              ...(isMobile ? {} : centeredFieldStyles),
            },
          ]
        : []),
      {
        name: 'username',
        type: FieldTypes.Text,
        label: 'Username',
        isLarge: true,
        placeholder: 'Username',
        enterKeyHint: 'next',
        showRequireIcon: true,
        validator: VALIDATORS.USER_NAME,
        isLowerCaseMode: true,
        ...(isMobile ? {} : centeredFieldStyles),
      },
      ...(!isOrphanMode
        ? [
            {
              name: 'phonenumber',
              type: FieldTypes.PhoneNumber,
              showRequireIcon: true,
              label: 'Phone number',
              enterKeyHint: 'next',
              placeholder: '+1',
              isLarge: true,
              validator: yup.string().test(validatePhoneNumber()).required(t('Phone number is required')),
              ...(isMobile ? {} : centeredFieldStyles),
            },
          ]
        : []),
    ],
    [isOrphanMode, t, email, isMobile]
  );
  return fields as Field[];
};
